import { EspressoMintTransactionMoleculeReatom } from "@/app/vendors/espresso/espresso-mint-molecule.reatom"
import { ThirdwebMintTransactionMoleculeReatom } from "@/app/vendors/thirdweb/thirdweb-mint-molecule"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import { DropType, type MinimalDrop } from "@anaconda/core/drop/types"
import { useMolecule } from "bunshi/react"
import { useCallback, useRef } from "react"
import { useSwitchEvmChain } from "../../use-switch-evm-chain"
import { MintTransactionMoleculeReatom } from "./evm-mint-molecule.reatom"

export interface MintSuccessResult {
  txId: string
  drop: MinimalDrop
  quantity?: number
}

export interface UseEvmMintOptions {
  onMintSuccess?: (result: MintSuccessResult) => void
}

export function useEvmMint(drop: MinimalDrop, options?: UseEvmMintOptions) {
  const isThirdwebDrop = drop.type === DropType.Thirdweb
  const isMultiChainDrop = drop.type === DropType.MultiChain
  const onMintSuccessRef = useRef(options?.onMintSuccess)
  onMintSuccessRef.current = options?.onMintSuccess

  // Use the appropriate molecule based on drop type
  const { mintAction: v0MintAction } = useMolecule(
    MintTransactionMoleculeReatom,
  )
  const { mintAction: thirdwebMintAction } = useMolecule(
    ThirdwebMintTransactionMoleculeReatom,
  )
  const { mintAction: espressoMintAction } = useMolecule(
    EspressoMintTransactionMoleculeReatom,
  )

  // Select the appropriate action based on drop type
  const mintAction = isThirdwebDrop
    ? thirdwebMintAction
    : isMultiChainDrop
      ? espressoMintAction
      : v0MintAction

  const isPending = !!mintAction.pending()

  const onSuccess = useCallback(
    (args?: { quantity?: number; blockchain?: Blockchain }) => {
      if (isThirdwebDrop) {
        thirdwebMintAction({
          drop,
          quantity: args?.quantity,
          onSuccess: onMintSuccessRef.current,
        })
      } else if (isMultiChainDrop) {
        espressoMintAction({
          drop,
          quantity: args?.quantity,
          blockchain: args?.blockchain,
        })
      } else {
        v0MintAction({ drop })
      }
    },
    [
      isThirdwebDrop,
      isMultiChainDrop,
      thirdwebMintAction,
      espressoMintAction,
      v0MintAction,
      drop,
    ],
  )

  const { action, switchInProgress } = useSwitchEvmChain(
    drop.blockchain,
    onSuccess,
  )

  return { mutate: action, isPending: isPending || switchInProgress }
}
